import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Navbar } from '../components/layout/Navbar';
import { 
  Sparkles, 
  Target, 
  TrendingUp, 
  BookOpen, 
  Upload, 
  Award,
  ChevronRight,
  Play,
  Palette,
  Layers,
  Zap,
  Flame,
  Trophy,
  Youtube
} from 'lucide-react';

export default function LandingPage() {
  const features = [
    {
      icon: Target,
      title: 'Adaptive Diagnostic',
      description: 'AI-powered assessment combining questionnaire responses with computer vision analysis to identify your specific skill gaps.',
      color: 'hsl(234 89% 63%)'
    },
    {
      icon: Upload,
      title: 'Artwork Analysis',
      description: 'Upload your reference art for detailed AI feedback on technique, composition, anatomy, and style-specific elements.',
      color: 'hsl(262 83% 58%)'
    },
    {
      icon: BookOpen,
      title: 'Dynamic Roadmap',
      description: 'Get a hyper-personalized, non-linear curriculum with 15, 30, and 60-minute exercises that adapt to your progress.',
      color: 'hsl(12 95% 65%)'
    },
    {
      icon: TrendingUp,
      title: 'Skill Metrics',
      description: 'Track your improvement with quantifiable skill metrics across shading, perspective, anatomy, and more.',
      color: 'hsl(234 89% 63%)'
    },
    {
      icon: Youtube,
      title: 'Tutorial Recommendations',
      description: 'Get curated YouTube tutorial suggestions based on your current exercises and learning objectives.',
      color: 'hsl(262 83% 58%)'
    },
    {
      icon: Trophy,
      title: 'Streaks & Milestones',
      description: 'Build daily practice habits with streak tracking and unlock rewards at key milestones.',
      color: 'hsl(12 95% 65%)'
    }
  ];

  const styleTracks = [
    { name: 'Beginner', desc: 'Foundational skills', icon: '🎨' },
    { name: 'Concept Art', desc: 'Game & film industry', icon: '🎮' },
    { name: 'Anime/Manga', desc: 'Japanese animation', icon: '✨' },
    { name: 'Realism', desc: 'Photorealistic rendering', icon: '📸' }
  ];

  return (
    <div className="min-h-screen bg-[hsl(220,20%,97%)]" data-testid="landing-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="hero-gradient pt-24 pb-20 px-4 sm:px-6 lg:px-8" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="animate-fade-in">
              <div className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-sm border border-slate-200/60 mb-6">
                <Sparkles className="w-4 h-4 text-[hsl(234,89%,63%)]" />
                <span className="text-sm font-medium text-slate-600">AI-Powered Art Mentorship</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-[hsl(222,47%,11%)] tracking-tight mb-6 font-['Outfit']">
                Your Personal
                <span className="gradient-text block">AI Art Mentor</span>
              </h1>
              
              <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-lg">
                Diagnose skill gaps, generate personalized learning paths, and track your progress 
                toward artistic mastery with adaptive AI guidance.
              </p>

              <div className="flex flex-wrap gap-4 mb-10">
                <Link to="/questionnaire" data-testid="hero-cta-primary">
                  <Button className="bg-[hsl(234,89%,63%)] hover:bg-[hsl(234,89%,58%)] text-white rounded-full h-12 px-8 shadow-lg hover:shadow-xl transition-all text-base font-medium">
                    Start Your Journey
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href="https://youtu.be/z1zLJQwNV8Y?si=5Rpd0sZ8MGKtkXv0" target="_blank" rel="noopener noreferrer">
                  <Button 
                    variant="outline" 
                    className="rounded-full h-12 px-8 border-2 border-slate-200 hover:border-[hsl(234,89%,63%)] hover:text-[hsl(234,89%,63%)] text-base font-medium"
                    data-testid="hero-cta-secondary"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Watch Demo
                  </Button>
                </a>
              </div>

              {/* Style Tracks Preview */}
              <div className="space-y-3">
                <span className="text-sm font-medium text-slate-500 uppercase tracking-wide">Style Tracks</span>
                <div className="flex flex-wrap gap-2">
                  {styleTracks.map((track) => (
                    <span key={track.name} className="art-style-tag flex items-center gap-1.5">
                      <span>{track.icon}</span>
                      {track.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Content - Hero Image */}
            <div className="relative animate-slide-in-right">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1611241893603-3c359704e0ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxkaWdpdGFsJTIwYXJ0aXN0JTIwd29ya2luZyUyMHRhYmxldHxlbnwwfHx8fDE3NzMyNDczNTF8MA&ixlib=rb-4.1.0&q=85"
                  alt="Digital artist working on tablet"
                  className="w-full h-[420px] object-cover"
                  data-testid="hero-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[hsl(222,47%,11%)]/70 via-transparent to-transparent" />
                
                {/* Floating Stats Card */}
                <div className="absolute bottom-6 left-6 right-6 glass rounded-xl p-5">
                  <div className="flex items-center justify-between">
                    <div className="text-center">
                      <p className="text-xs text-slate-500 mb-1">Artists</p>
                      <p className="text-xl font-bold text-[hsl(222,47%,11%)]">10K+</p>
                    </div>
                    <div className="h-10 w-px bg-slate-200" />
                    <div className="text-center">
                      <p className="text-xs text-slate-500 mb-1">Skill Growth</p>
                      <p className="text-xl font-bold text-[hsl(222,47%,11%)]">85%</p>
                    </div>
                    <div className="h-10 w-px bg-slate-200" />
                    <div className="text-center">
                      <p className="text-xs text-slate-500 mb-1">Style Tracks</p>
                      <p className="text-xl font-bold text-[hsl(222,47%,11%)]">4+</p>
                    </div>
                    <div className="h-10 w-px bg-slate-200" />
                    <div className="text-center">
                      <p className="text-xs text-slate-500 mb-1">Exercises</p>
                      <p className="text-xl font-bold text-[hsl(222,47%,11%)]">500+</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-[hsl(234,89%,63%)] tracking-wide uppercase">How It Works</span>
            <h2 className="text-4xl md:text-5xl font-semibold text-[hsl(222,47%,11%)] mt-3 mb-4 font-['Outfit']">
              Your Path to Mastery
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Four steps to transform your artistic journey with AI-powered guidance
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Select Style Track', desc: 'Choose your artistic focus: Beginner, Concept Art, Anime/Manga, or Realism.', icon: Palette },
              { step: '02', title: 'Complete Diagnostic', desc: 'Answer a short intake questionnaire for AI skill gap analysis.', icon: Target },
              { step: '03', title: 'Get Your Roadmap', desc: 'Receive a personalized curriculum with exercises tailored to you.', icon: BookOpen },
              { step: '04', title: 'Track & Improve', desc: 'Complete exercises, track skill metrics, and watch yourself grow.', icon: TrendingUp }
            ].map((item, idx) => (
              <div key={idx} className="relative group" data-testid={`step-card-${idx + 1}`}>
                <div className="card-elevated rounded-2xl p-6 h-full">
                  <div className="flex items-center gap-3 mb-5">
                    <span className="text-4xl font-bold text-slate-100 font-['Outfit']">{item.step}</span>
                    <div className="w-10 h-10 rounded-xl bg-[hsl(234,89%,63%)]/10 flex items-center justify-center group-hover:bg-[hsl(234,89%,63%)] transition-colors">
                      <item.icon className="w-5 h-5 text-[hsl(234,89%,63%)] group-hover:text-white transition-colors" />
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-[hsl(222,47%,11%)] mb-2 font-['Outfit']">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-[hsl(234,89%,63%)] tracking-wide uppercase">Features</span>
            <h2 className="text-4xl md:text-5xl font-semibold text-[hsl(222,47%,11%)] mt-3 mb-4 font-['Outfit']">
              Everything You Need to Grow
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive AI tools designed for visual artists at every skill level
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 stagger-children">
            {features.map((feature, idx) => (
              <Card 
                key={idx} 
                className="card-elevated border-0"
                data-testid={`feature-card-${idx + 1}`}
              >
                <CardContent className="p-6">
                  <div 
                    className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${feature.color.replace(')', ' / 0.1)')}` }}
                  >
                    <feature.icon className="w-5 h-5" style={{ color: feature.color }} />
                  </div>
                  <h3 className="text-lg font-semibold text-[hsl(222,47%,11%)] mb-2 font-['Outfit']">{feature.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Streak & Gamification Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="gamification-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm font-semibold text-[hsl(12,95%,65%)] tracking-wide uppercase">Stay Motivated</span>
              <h2 className="text-4xl font-semibold text-[hsl(222,47%,11%)] mt-3 mb-4 font-['Outfit']">
                Build Habits That Stick
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Our streak system and community features keep you accountable and motivated on your artistic journey.
              </p>
              
              <div className="space-y-4">
                {[
                  { icon: Flame, title: 'Daily Streaks', desc: 'Track consecutive days of practice and earn rewards at 7, 30, and 100-day milestones.' },
                  { icon: Award, title: 'Achievement Badges', desc: 'Unlock badges as you reach skill milestones and complete modules.' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[hsl(12,95%,65%)]/10 flex items-center justify-center shrink-0">
                      <item.icon className="w-5 h-5 text-[hsl(12,95%,65%)]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[hsl(222,47%,11%)]">{item.title}</h4>
                      <p className="text-sm text-slate-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-[hsl(222,47%,11%)] rounded-2xl p-8 shadow-xl">
                <div className="text-center mb-6">
                  <Flame className="w-16 h-16 text-[hsl(12,95%,65%)] mx-auto mb-4 streak-fire" />
                  <p className="text-6xl font-bold text-white font-['Outfit']">45</p>
                  <p className="text-slate-400">Day Streak</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {[
                    { label: '7 Days', earned: true },
                    { label: '30 Days', earned: true },
                    { label: '100 Days', earned: false }
                  ].map((milestone, idx) => (
                    <div 
                      key={idx} 
                      className={`rounded-lg p-3 text-center ${
                        milestone.earned 
                          ? 'bg-[hsl(12,95%,65%)]/20 border border-[hsl(12,95%,65%)]/30' 
                          : 'bg-white/5 border border-white/10'
                      }`}
                    >
                      <Award className={`w-6 h-6 mx-auto mb-1 ${milestone.earned ? 'text-[hsl(12,95%,65%)]' : 'text-slate-500'}`} />
                      <p className={`text-xs font-medium ${milestone.earned ? 'text-white' : 'text-slate-500'}`}>
                        {milestone.label}
                      </p>
                    </div>
                  ))}
                </div>
                
                <p className="text-center text-slate-400 text-sm">
                  55 days until your next milestone reward!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="cta-section">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[hsl(222,47%,11%)] rounded-3xl p-12 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-72 h-72 bg-[hsl(234,89%,63%)]/15 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-72 h-72 bg-[hsl(262,83%,58%)]/15 rounded-full blur-3xl" />
            
            <div className="relative z-10">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[hsl(234,89%,63%)] to-[hsl(262,83%,58%)] flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Palette className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-['Outfit']">
                Ready to Transform Your Art?
              </h2>
              <p className="text-slate-300 mb-8 max-w-lg mx-auto">
                Join thousands of artists who've accelerated their growth with personalized AI mentorship.
              </p>
              <Link to="/questionnaire" data-testid="cta-button">
                <Button className="bg-white text-[hsl(222,47%,11%)] hover:bg-slate-100 rounded-full h-12 px-8 text-base font-semibold shadow-lg hover:shadow-xl transition-all">
                  <Sparkles className="w-5 h-5 mr-2 text-[hsl(234,89%,63%)]" />
                  Start Free Assessment
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[hsl(222,47%,11%)] py-12 px-4 sm:px-6 lg:px-8" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[hsl(234,89%,63%)] to-[hsl(262,83%,58%)] flex items-center justify-center">
                <Palette className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-white font-['Outfit']">
                Skill<span className="text-[hsl(262,83%,58%)]">Skulpt</span>
              </span>
            </div>
            <p className="text-slate-400 text-sm">
              © 2024 SkillSkulpt. Empowering artists with AI.
            </p>
            <div className="flex gap-6">
              <a href="#" className="text-slate-400 hover:text-white text-sm transition-colors">Privacy</a>
              <a href="#" className="text-slate-400 hover:text-white text-sm transition-colors">Terms</a>
              <a href="#" className="text-slate-400 hover:text-white text-sm transition-colors">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
